import { ErrorBoundary } from '@components/feedback/ErrorBoundary';
import { RouteErrorBoundary } from '@components/feedback/RouteErrorBoundary';
import { AuthLayout } from '@components/layout/AuthLayout';
import { MainLayout } from '@components/layout/MainLayout';
import { LoadingSpinner } from '@components/ui/LoadingSpinner';
import {
  DashboardSkeleton,
  DetailPageSkeleton,
  FormSkeleton,
  ListPageSkeleton,
} from '@components/ui/skeleton';
import { ROUTES } from '@constants/routes';
import { useAuthStore } from '@store/authStore';
import { Suspense, lazy } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';

import { GuestRoute } from './GuestRoute';
import { ProtectedRoute } from './ProtectedRoute';
import { CentreRequiredRoute } from './CentreRequiredRoute';
import { RoleRoute } from './RoleRoute';
import { SingleCentreRoute } from './SingleCentreRoute';

const LoginPage = lazy(() => import('@pages/auth/LoginPage'));
const ForgotPasswordPage = lazy(() => import('@pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@pages/auth/ResetPasswordPage'));
const AdmissionsPage = lazy(() => import('@pages/admissions/AdmissionsPage'));
const AddAdmissionPage = lazy(() => import('@pages/admissions/AddAdmissionPage'));
const InstallmentsPage = lazy(() => import('@pages/installments/InstallmentsPage'));
const AddInstallmentPage = lazy(() => import('@pages/installments/AddInstallmentPage'));
const StudentDetailPage = lazy(() => import('@pages/shared/StudentDetailPage'));
const DashboardPage = lazy(() => import('@pages/dashboard/DashboardPage'));
const UsersPage = lazy(() => import('@pages/users/UsersPage'));
const StudentsPage = lazy(() => import('@pages/students/StudentsPage'));
const SettingsPage = lazy(() => import('@pages/settings/SettingsPage'));
const ReportsPage = lazy(() => import('@pages/reports/ReportsPage'));
const NotFoundPage = lazy(() => import('@pages/NotFoundPage'));

/**
 * Route-level fallback while a lazy page chunk downloads.
 *
 * This has to be the SAME shape the page shows while it fetches, otherwise a
 * first visit flashes twice — a spinner for the chunk, then a skeleton for the
 * data. Giving each route the skeleton matching its own layout makes those one
 * continuous placeholder.
 */
const withSuspense = (element: React.ReactNode, fallback: React.ReactNode) => (
  <Suspense fallback={fallback}>{element}</Suspense>
);

// The two dashboards differ in shape, so the chunk-loading placeholder follows
// the signed-in role exactly as DashboardPage's own loading state does.
const DashboardFallback = () => {
  const role = useAuthStore((s) => s.user?.role);
  return <DashboardSkeleton variant={role === 'admin' ? 'admin' : 'staff'} />;
};

// Auth pages render outside the app shell on their own split layout, where a
// list/table skeleton would be misleading — a centred spinner is right there.
const authFallback = <LoadingSpinner fullScreen />;

const router = createBrowserRouter([
  {
    // Shared across every route below — catches stale-chunk failures from
    // lazy-loaded pages (see RouteErrorBoundary) as well as any other
    // unhandled render/loader error, since react-router's own default error
    // page would otherwise pre-empt the app's ErrorBoundary.
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: ROUTES.ROOT,
        element: <Navigate to={ROUTES.DASHBOARD} replace />,
      },
      {
        element: <GuestRoute />,
        children: [
          {
            element: <AuthLayout />,
            children: [
              { path: ROUTES.LOGIN, element: withSuspense(<LoginPage />, authFallback) },
              {
                path: ROUTES.FORGOT_PASSWORD,
                element: withSuspense(<ForgotPasswordPage />, authFallback),
              },
              {
                path: ROUTES.RESET_PASSWORD,
                element: withSuspense(<ResetPasswordPage />, authFallback),
              },
            ],
          },
        ],
      },
      {
        element: <ProtectedRoute />,
        children: [
          {
            element: <MainLayout />,
            children: [
              // Nothing works without at least one centre to scope to, so every
              // page sits behind CentreRequiredRoute. Within that, Dashboard and
              // Reports understand the top bar's "All Centres" rollup; everything
              // else needs one specific centre and adds SingleCentreRoute.
              {
                element: <CentreRequiredRoute />,
                children: [
                  {
                    path: ROUTES.DASHBOARD,
                    element: withSuspense(<DashboardPage />, <DashboardFallback />),
                  },
                  {
                    element: <SingleCentreRoute />,
                    children: [
                      {
                        path: ROUTES.ADMISSIONS,
                        element: withSuspense(
                          <AdmissionsPage />,
                          <ListPageSkeleton stats={3} columns={10} />,
                        ),
                      },
                      {
                        path: ROUTES.ADMISSIONS_NEW,
                        element: withSuspense(<AddAdmissionPage />, <FormSkeleton fields={12} />),
                      },
                      {
                        path: ROUTES.INSTALLMENTS,
                        element: withSuspense(
                          <InstallmentsPage />,
                          <ListPageSkeleton stats={5} columns={9} />,
                        ),
                      },
                      {
                        path: ROUTES.INSTALLMENTS_NEW,
                        element: withSuspense(<AddInstallmentPage />, <FormSkeleton fields={9} />),
                      },
                      {
                        path: ROUTES.INSTALLMENTS_TRANSACTIONS,
                        element: withSuspense(<StudentDetailPage />, <DetailPageSkeleton />),
                      },
                      {
                        path: ROUTES.ADMISSIONS_STUDENT_DETAIL,
                        element: withSuspense(<StudentDetailPage />, <DetailPageSkeleton />),
                      },
                      {
                        path: ROUTES.STUDENTS,
                        element: withSuspense(
                          <StudentsPage />,
                          <ListPageSkeleton stats={0} columns={7} />,
                        ),
                      },
                      {
                        path: ROUTES.STUDENT_DETAIL,
                        element: withSuspense(<StudentDetailPage />, <DetailPageSkeleton />),
                      },
                      {
                        path: ROUTES.SETTINGS,
                        element: withSuspense(<SettingsPage />, <FormSkeleton fields={6} />),
                      },
                    ],
                  },
                  {
                    element: <RoleRoute requiredRole="admin" />,
                    children: [
                      {
                        path: ROUTES.REPORTS,
                        element: withSuspense(
                          <ReportsPage />,
                          <ListPageSkeleton stats={4} columns={12} />,
                        ),
                      },
                      {
                        element: <SingleCentreRoute />,
                        children: [
                          {
                            path: ROUTES.USERS,
                            element: withSuspense(
                              <UsersPage />,
                              <ListPageSkeleton stats={0} columns={5} />,
                            ),
                          },
                          {
                            path: ROUTES.ADMISSIONS_EDIT,
                            element: withSuspense(
                              <AddAdmissionPage />,
                              <FormSkeleton fields={12} />,
                            ),
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      { path: ROUTES.NOT_FOUND, element: withSuspense(<NotFoundPage />, authFallback) },
    ],
  },
]);

export const AppRouter = () => (
  <ErrorBoundary>
    <RouterProvider router={router} />
  </ErrorBoundary>
);
